import type { MouseEventHandler, ReactNode } from 'react';

import type { Nil, Unit, UserPreferredUnit } from '@core';

import { Stack, type BoxProps } from '../../Layout';
import { ParameterCard } from '../ParameterCard';
import { ParameterStats } from '../ParameterStats';
import type { CorridorBounds } from '../types';
import { useParameters } from '../useParameters';

export type SwingFoundationParameterCardProps = {
    title: string;
    unit: Unit;
    userPreferredUnit: UserPreferredUnit;
    value: number | Nil;
    comparisonValue?: number | Nil;
    corridorParameterMargins?: CorridorBounds | Nil;
    goalCorridor?: CorridorBounds | Nil;
    mean?: number | Nil;
    stdDev?: number | Nil;
    menuItems?: ReactNode;
    shadow?: BoxProps['shadow'];
    onClick?: MouseEventHandler<HTMLDivElement>;
};

export const SwingFoundationParameterCard = ({
    title,
    unit,
    userPreferredUnit,
    value,
    comparisonValue,
    corridorParameterMargins,
    goalCorridor,
    mean,
    stdDev,
    menuItems,
    shadow,
    onClick,
}: SwingFoundationParameterCardProps) => {
    const converted = useParameters({
        unit,
        value,
        comparisonValue,
        corridorParameterMargins,
        goalCorridorParameterMargins: goalCorridor,
        userPreferredUnit,
        stdDev,
        mean,
    });
    const indicatorColor = goalCorridor ? converted.goalIndicatorColor : converted.corridorIndicatorColor;

    const endSlot = menuItems ? <ParameterCard.Menu>{menuItems}</ParameterCard.Menu> : undefined;

    return (
        <Stack gap={0}>
            <ParameterCard.Root
                shadow={shadow}
                onClick={onClick}
                corridors={
                    <ParameterCard.SingleCorridor
                        corridorParameterMargins={converted.corridorParameterMargins}
                        goalCorridor={converted.goalCorridor}
                        unit={converted.unit}
                        value={converted.value}
                        indicatorColor={indicatorColor}
                    />
                }
            >
                <ParameterCard.Title endSlot={endSlot}>{title}</ParameterCard.Title>
                <ParameterCard.Values>
                    <ParameterCard.Value
                        displayUnit={converted.unit}
                        symbol={converted.symbol}
                        value={converted.value}
                        indicatorColor={indicatorColor}
                    />
                    <ParameterCard.Comparison
                        displayUnit={converted.unit}
                        symbol={converted.symbol}
                        value={converted.comparison}
                    />
                </ParameterCard.Values>
            </ParameterCard.Root>
            <ParameterStats mean={converted.mean} stdDev={converted.stdDev} />
        </Stack>
    );
};
